import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import CTNTable from '@ctn/ui/components/ctn-table';
import { useMemo, useState } from 'react';

import useFetchDocument from '@/hooks/useFetchDocument';

import { MIMETYPE_PDF } from '@/constants/file.constants';
import { type Action, makeActions } from '@/helpers/table';
import { type IDocumento } from '@/interfaces/model/don.interface';
import { downloadFile } from '@/utils/file.utils';

import ModalVisorPdf from '../ModalVisorPdf';
import { getColumns, isSolicitanteOrigin } from './columns';

type BaseProps = {
	readonly documentos: IDocumento[];
	readonly confirmDeleteDoc?: (value: IDocumento) => void;
	readonly onViewDocumento?: (value: IDocumento) => void;
	readonly onSubsanar?: (value: IDocumento) => void;
	readonly hasColumnSubsanacion?: boolean;
	readonly noData?: string;
};

type PropsWithSolicitud = BaseProps & {
	readonly idSolicitud: number;
	readonly idTramite?: never;
};

type PropsWithTramite = BaseProps & {
	readonly idTramite: number;
	readonly idSolicitud?: never;
};

type DocumentosListadoProps = PropsWithSolicitud | PropsWithTramite;

const DocumentosListado = ({
	documentos,
	confirmDeleteDoc,
	idSolicitud,
	idTramite,
	onSubsanar,
	hasColumnSubsanacion,
	noData,
	onViewDocumento,
}: DocumentosListadoProps) => {	
	const [dataModalPdf, setDataModalPdf] = useState<IDocumento | undefined>(undefined);
	const { isShowing, openModal, closeModal } = useModal({});
	const fetchConfig = idSolicitud ? { idSolicitud } : { idTramite: idTramite as number };
	const { fetchDocument } = useFetchDocument(fetchConfig);

	const onPreviewDocument = (value: IDocumento) => {
		const newValue = { ...value, tamano: value.tamano };
		setDataModalPdf(newValue);
		openModal();
	};

	const onDownloadDocumento = async (a: IDocumento) => {
		const { contenidoBase64: contenidoBase64Internal, nombre, mimeType, id } = a;
		let contenidoBase64 = contenidoBase64Internal;
		if (id) {
			const response = await fetchDocument(id);
			if (response?.data) {
				contenidoBase64 = response?.data;
			}
		}
		downloadFile({ data: contenidoBase64, name: nombre, mimeType: mimeType.nombre });
	};

	/**
	 * Compara dos strings respetando acentos, eñes y mayúsculas/minúsculas.
	 */
	const compareStrings = (a: string, b: string): number =>
		(a || '').localeCompare(b || '', 'es', { sensitivity: 'accent' });

	const documentosSorted = [...documentos].sort(
		(a, b) =>
			// 1. Primer criterio: Tipo de Documento
			compareStrings(a.tipoDocumento.descripcion, b.tipoDocumento.descripcion) ||
			// 2. Segundo criterio: Nombre del archivo (alfabético dentro del mismo grupo)
			compareStrings(a.nombre, b.nombre),
	);

	const columns = useMemo(() => {
		const actions: Array<Action<IDocumento>> = [
			// Acción de ver documento en una página, reemplaza la visualización del documento.
			{
				width: 140,
				label: 'Ver documento',
				command: onViewDocumento ?? onPreviewDocument,
				hideIf:  ({ mimeType }) => !onViewDocumento && mimeType.nombre !== MIMETYPE_PDF,
			},
			// Acción de descargar cualquier documento que no sea PDF
			{
				width: 160,
				label: 'Descargar',
				command(doc: IDocumento) {
					onDownloadDocumento(doc);
				},
			},
			{
				width: 100,
				label: 'Subsanar',
				command: onSubsanar,
				hideIf: ({ subsanacionPendiente, origenDocumento }) => !subsanacionPendiente || !isSolicitanteOrigin(origenDocumento),
			},
			{ width: 100, label: 'Eliminar', command: confirmDeleteDoc, hideIf: ({origenDocumento}) => !confirmDeleteDoc || !isSolicitanteOrigin(origenDocumento)},
		];

		const columnsWithOutActions = getColumns({
			hasColumnSubsanacion,
		});

		const actionsColumn = makeActions<IDocumento>({ data: documentosSorted, actions });

		return [...columnsWithOutActions, actionsColumn];
	}, [documentosSorted, hasColumnSubsanacion]);

	return (
		<>
			<CTNTable
				idData="id"
				loading={false}
				loadingConf={{ spinner: true }}
				className="c-pnp-table"
				columns={columns}
				data={documentosSorted}
				totalData={documentosSorted.length}
				noDataConf={{ description: noData ?? 'Aún no has añadido ningún documento' }}
				pageSize={100}
			/>
			<ModalVisorPdf
				isOpen={isShowing}
				data={dataModalPdf}
				{...(idSolicitud ? { idSolicitud } : { idTramite: idTramite as number })}
				onClose={closeModal}
			/>
		</>
	);
};

export default DocumentosListado;
